import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import { TimeField } from "@mui/x-date-pickers";
import { CloudUpload, Delete } from "@mui/icons-material";

import { useMotivosAusencia } from "../hooks/useCalendario";
import { useAusencias, Ausencia } from "../hooks/useAusencias";
import api from "../api/axios";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  absence: Ausencia | null;
  onClose: () => void;
  onSaved: () => void;
  canApprove?: boolean; // HR/Admin = true · Profesor = false/undefined
}

export default function DlgAusenciaEdit({
  open,
  absence,
  onClose,
  onSaved,
  canApprove = false,
}: Props) {
  const { t, i18n } = useTranslation<"translation">();
  const { data: motivos = [] } = useMotivosAusencia();
  const { update } = useAusencias(absence?.idProfesor ?? 0);

  /* ─────────────────── formulario ─────────────────── */
  const [form, setForm] = useState<{
    motivoId: number | null;
    tipo: "justificada" | "injustificada";
    tramo: "dia" | "horas";
    horaInicio: Dayjs | null;
    horaFin: Dayjs | null;
    nota: string;
  }>({
    motivoId: null,
    tipo: "injustificada",
    tramo: "dia",
    horaInicio: dayjs("09:00", "HH:mm"),
    horaFin: dayjs("13:00", "HH:mm"),
    nota: "",
  });

  useEffect(() => {
    if (!absence) return;
    setForm({
      motivoId: absence.motivo.id,
      tipo: absence.tipo,
      tramo: absence.horaInicio == null ? "dia" : "horas",
      horaInicio: absence.horaInicio ? dayjs(absence.horaInicio, "HH:mm") : dayjs("09:00", "HH:mm"),
      horaFin: absence.horaFin ? dayjs(absence.horaFin, "HH:mm") : dayjs("13:00", "HH:mm"),
      nota: "",
    });
  }, [absence]);

  /* ─────────────────── guardar ─────────────────── */
  const handleSave = async () => {
    if (!absence) return;

    await update(absence.id, {
      motivoId: form.motivoId!,
      ...(canApprove && { tipo: form.tipo }),
      horaInicio: form.tramo === "dia" ? null : form.horaInicio!.format("HH:mm"),
      horaFin: form.tramo === "dia" ? null : form.horaFin!.format("HH:mm"),
    });

    onSaved();
    onClose();
  };

  /* ─────────────────── acciones ─────────────────── */
  const handleCancelRequest = async () => {
    if (!absence) return;
    // backend: cancela o anula la ausencia
    await api.delete(`/ausencias/${absence.id}/cancelar`);
    onSaved();
    onClose();
  };

  const handleReject = async () => {
    if (!absence) return;
    await api.delete(`/ausencias/${absence.id}/cancelar`);
    onSaved();
    onClose();
  };

  const handleApprove = async () => {
    if (!absence) return;
    await api.put(`/ausencias/${absence.id}/aprobar`);
    onSaved();
    onClose();
  };

  const handleConvert = async () => {
    if (!absence) return;
    await api.post(`/ausencias/${absence.id}/convertir-vacacion`);
    onSaved();
    onClose();
  };

  /* ─────────────────── adjuntos ─────────────────── */
  const [files, setFiles] = useState<{ id: number; filename: string }[]>([]);

  const loadAdjuntos = async () => {
    if (!absence) return;
    const { data } = await api.get(`/ausencias/${absence.id}/adjuntos`);
    setFiles(data);
  };
  useEffect(() => {
    if (open) loadAdjuntos();
  }, [open]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    try {
      if (!absence || !input.files || input.files.length === 0) return;

      const file = input.files[0];

      // Validaciones rápidas en cliente (no confiar 100%, el backend valida por firma mágica)
      const MAX_BYTES = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX_BYTES) {
        alert(t("file.maxSize", "Máximo 5MB"));
        return;
      }
      const allowedMimes = ["application/pdf", "image/png", "image/jpeg"];
      const okMime = allowedMimes.includes(file.type);
      const okExt = /\.(pdf|png|jpe?g)$/i.test(file.name);
      if (!(okMime || okExt)) {
        alert(t("file.badType", "Tipo de archivo no permitido (PDF/JPG/PNG)"));
        return;
      }

      const fd = new FormData();
      fd.append("file", file);

      await api.post(`/ausencias/${absence.id}/adjuntos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Si usas react-toastify, puedes usar:
      // toast.success(t('file.uploadOk', 'Archivo subido'));
      await loadAdjuntos();
    } catch (err) {
      // Los errores del servidor (400/413/415/…) ya los muestra el interceptor de axios.
      // Aquí solo evitamos que burbujeen y rompan el render.
      // Si quieres, puedes loguearlo:
    } finally {
      // Reset del input para permitir volver a seleccionar el mismo archivo
      input.value = "";
    }
  };

  const handleDeleteFile = async (id: number) => {
    await api.delete(`/adjuntos/${id}`);
    loadAdjuntos();
  };

  const openAdjunto = async (id: number) => {
    const { data, headers } = await api.get(`/adjuntos/${id}`, {
      responseType: "blob",
    });
    const blob = new Blob([data], {
      type: headers["content-type"] || "application/octet-stream",
    });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  /* ─────────────────── reglas visibilidad ─────────────────── */
  const hoy = dayjs().startOf("day");

  // Profesor/HR puede cancelar mientras esté pendiente y sea futura
  const puedeCancelarPendiente =
    absence && absence.estado !== "approved" && dayjs(absence.fecha).isAfter(hoy);

  // HR/Admin puede anular incluso aprobadas
  const puedeAnularAprobada = canApprove && absence && absence.estado === "approved";

  /* ─────────────────── render ─────────────────── */
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {absence && dayjs(absence.fecha).locale(i18n.language).format("LL")}
      </DialogTitle>

      {absence && (
        <>
          {/* ---------- CONTENIDO ---------- */}
          <DialogContent sx={{ pt: 1 }}>
            {/* MOTIVO */}
            <TextField
              select
              fullWidth
              margin="normal"
              label={t("absence.motivo")}
              value={form.motivoId ?? ""}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  motivoId: Number(e.target.value) || null,
                }))
              }
              disabled={!canApprove}
            >
              {motivos.map((m: { id: number; nombreEs: string }) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nombreEs}
                </MenuItem>
              ))}
            </TextField>

            {/* JUSTIFICADA / INJUSTIFICADA (HR) */}
            {canApprove && (
              <RadioGroup
                row
                value={form.tipo}
                onChange={(_, v) => setForm((s) => ({ ...s, tipo: v as any }))}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="justificada"
                  control={<Radio />}
                  label={t("absence.justificada")}
                />
                <FormControlLabel
                  value="injustificada"
                  control={<Radio />}
                  label={t("absence.injustificada")}
                />
              </RadioGroup>
            )}

            {/* TRAMO (HR) */}
            {canApprove && (
              <RadioGroup
                row
                value={form.tramo}
                onChange={(_, v) => setForm((s) => ({ ...s, tramo: v as any }))}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="dia"
                  control={<Radio />}
                  label={t("absence.diaCompleto")}
                />
                <FormControlLabel value="horas" control={<Radio />} label={t("absence.horas")} />
              </RadioGroup>
            )}

            {canApprove && form.tramo === "horas" && (
              <Stack direction="row" spacing={2}>
                <TimeField
                  label={t("absence.inicio")}
                  value={form.horaInicio}
                  onChange={(v) => setForm((s) => ({ ...s, horaInicio: v as Dayjs }))}
                />
                <TimeField
                  label={t("absence.fin")}
                  value={form.horaFin}
                  onChange={(v) => setForm((s) => ({ ...s, horaFin: v as Dayjs }))}
                />
              </Stack>
            )}

            {/* NOTA */}
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              margin="normal"
              label={t("absence.nota")}
              value={form.nota}
              onChange={(e) => setForm((s) => ({ ...s, nota: e.target.value }))}
            />

            {/* ADJUNTOS */}
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Button component="label" startIcon={<CloudUpload />}>
                {t("absence.subirAdjunto")}
                <input
                  hidden
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={handleUpload}
                />
              </Button>

              <List dense>
                {files.map((f) => (
                  <ListItem key={f.id}>
                    <ListItemText
                      primary={
                        <Button
                          variant="text"
                          sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                          }}
                          onClick={() => openAdjunto(f.id)}
                        >
                          {f.filename}
                        </Button>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleDeleteFile(f.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Stack>
          </DialogContent>

          {/* ---------- BOTONERA ---------- */}
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1.5}
              sx={{ width: "100%", justifyContent: "space-between" }}
            >
              {/* IZQUIERDA – acciones HR / convertir / cancelar */}
              <Stack direction="row" gap={1.5}>
                {/* HR: rechazar / aprobar si está pendiente */}
                {canApprove && absence.estado === "pending" && (
                  <>
                    <Button color="error" onClick={handleReject}>
                      {t("common.reject")}
                    </Button>
                    <Button color="success" variant="contained" onClick={handleApprove}>
                      {t("common.approve")}
                    </Button>
                  </>
                )}

                {/* HR: convertir en vacaciones */}
                {canApprove && (
                  <Button color="info" variant="contained" onClick={handleConvert}>
                    {t("absence.toVac", "Convertir en vacaciones")}
                  </Button>
                )}

                {/* Profesor / HR: cancelar solicitud pendiente futura */}
                {puedeCancelarPendiente && (
                  <Button color="error" onClick={handleCancelRequest}>
                    {t("common.cancelRequest", "Cancelar solicitud")}
                  </Button>
                )}

                {/* HR: anular ausencia aprobada */}
                {puedeAnularAprobada && (
                  <Button color="error" variant="outlined" onClick={handleCancelRequest}>
                    {t("absence.cancelApproved", "Anular ausencia")}
                  </Button>
                )}
              </Stack>

              {/* DERECHA – cerrar / guardar */}
              <Stack direction="row" gap={1.5}>
                <Button onClick={onClose}>{t("common.close", "Cerrar")}</Button>
                <Button variant="contained" onClick={handleSave}>
                  {t("common.save", "Guardar")}
                </Button>
              </Stack>
            </Stack>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
